import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {
  SECTIONS,
  SECTION_LABELS,
  SECTION_VIEW_PERMISSION,
  type Section,
} from "@xop/dashboard-shared";
import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { useSessionUser } from "../auth/AuthProvider.js";
import { useCanSeeSection } from "../auth/permissions.js";
import { api } from "../lib/api.js";
import { useChatOverview } from "../lib/chat/hooks.js";
import { ottoOriginFromBase } from "../lib/otto/tokenRefresh.js";
import { useOttoTokenRefresh } from "../lib/otto/useOttoTokenRefresh.js";
import { RealtimeProvider } from "../lib/notifications/RealtimeProvider.js";
import { useDeepLinkRouting } from "../lib/notifications/useDeepLinkRouting.js";
import { useTrayUnreadBadge } from "../lib/notifications/useTrayUnreadBadge.js";
import { CallProvider } from "../calls/CallProvider.js";
import { CallWindow } from "../calls/CallWindow.js";
import { CallLauncherButton } from "../calls/CallLauncherButton.js";
import { IncomingCallModal } from "../calls/IncomingCallModal.js";
import { useCallsEnabled } from "../calls/useCallsEnabled.js";
import { AgenticProvider } from "../otto-agent/AgenticProvider.js";
import { OttoAgentLauncher } from "../otto-agent/OttoAgentLauncher.js";
import { CloakOverlay } from "../otto-agent/CloakOverlay.js";
import {
  useOverdueInvoiceBadge,
  type OverdueBadge,
} from "../lib/crm/overdueBadge.js";
import { RouteErrorBoundary } from "./ErrorBoundary.js";
import { NotificationBell } from "./NotificationBell.js";
import {
  OverdueNavAlert,
  OverdueWave,
  overdueTabSx,
} from "./OverdueNavAlert.js";
import { PresenceRail } from "./PresenceRail.js";
import { ProfileMenu } from "./ProfileMenu.js";
import { TopLoadingBar } from "./TopLoadingBar.js";
import { Toaster } from "./ui/toaster.js";

/**
 * Renders the app-wide calling UI (incoming-call modal + in-call window).
 * Must be a child of both `<CallProvider>` and `<RealtimeProvider>`.
 * Gated on `useCallsEnabled()` so both are inert when calling is not
 * configured on the server.
 */
function CallUi(): JSX.Element | null {
  const callsEnabled = useCallsEnabled();
  if (!callsEnabled) return null;
  return (
    <>
      <IncomingCallModal />
      <CallWindow />
    </>
  );
}

/** Top-nav layout. Renders the horizontal section tabs the user has
 * permission to see (the `SECTIONS` order, Dashboard → … → Server →
 * Tester), plus a profile menu. The active tab is driven by the URL —
 * react-router's NavLink does the work for us. */
export function AppShell(): JSX.Element {
  const user = useSessionUser();
  const [ottoToken, setOttoToken] = useState<string | null>(null);

  // Below `md` the centered pill-tray can't fit the brand + 8 section tabs +
  // bell + profile, so it collapses to a hamburger that opens a left Drawer.
  // `defaultMatches: false` keeps the desktop tray as the first-render/SSR +
  // no-matchMedia fallback (so unit tests render the tray, not the drawer).
  const theme = useTheme();
  const isMobileNav = useMediaQuery(theme.breakpoints.down("md"), {
    noSsr: true,
    defaultMatches: false,
  });
  const [navOpen, setNavOpen] = useState(false);

  // Overdue-invoice alert for the CRM tab (dot + periodic wave). Read ONCE
  // here and handed to both the desktop tab and the mobile drawer row, so the
  // two surfaces can never disagree — and so the poll runs once per session,
  // not once per nav item. Inert (and un-fetched) for anyone without
  // `crm:view` + `crm:financials`. See lib/crm/overdueBadge.ts.
  const overdue = useOverdueInvoiceBadge();

  // Mirror the live unread-chat total onto the desktop tray icon (a baked-in
  // red count bubble). No-op in a plain browser; reuses the nav's deduped
  // chat/overview query so it costs no extra fetch.
  useTrayUnreadBadge();

  // Open the right record when the desktop app is launched/raised via a share
  // deep-link (`xopstaff://...`). No-op in a plain browser.
  useDeepLinkRouting();

  // Otto Platform — embedded staff widget (Phase 9). Mint the per-bot
  // session JWT HERE in the dashboard: /api/me/otto-token is bearer-
  // authenticated (same-origin via the api client), and the staff user
  // already has that bearer. The widget's iframe lives on the Otto host
  // origin (support.xopnetworks.com) and CANNOT carry the dashboard bearer,
  // so the old "iframe fetches the token cross-origin with cookies"
  // approach never authenticated. We pass the pre-minted token in via
  // data-chat-token instead — mirroring the working customer OttoWidget.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api<{ token: string }>("/api/me/otto-token").then(
      (res) => { if (!cancelled) setOttoToken(res.token); },
      () => { if (!cancelled) setOttoToken(null); },
    );
    return () => { cancelled = true; };
  }, [user]);

  // Inject the loader once we hold a token. VITE_OTTO_BASE_URL is baked
  // into the build (wired on the `web` service in docker-compose*.yml — a
  // missing wire is what once left this widget invisible in prod; see
  // docs/otto-bots.md + compose-otto-env.test.ts).
  useEffect(() => {
    if (typeof window === "undefined" || !ottoToken) return;
    if (document.querySelector("script[data-xop-otto-loader]")) return;
    const env = (import.meta as { env?: Record<string, string> }).env ?? {};
    const loader = buildOttoLoaderScript(env.VITE_OTTO_BASE_URL, ottoToken);
    if (!loader) return;
    document.body.appendChild(loader);
    return () => {
      const w = window as unknown as { __OTTO_DESTROY?: () => void };
      try { w.__OTTO_DESTROY?.(); } catch { /* ignore */ }
      document
        .querySelectorAll("script[data-xop-otto-loader]")
        .forEach((n) => n.remove());
    };
  }, [ottoToken]);

  // Keep the widget's short-lived session token fresh. The token above is
  // baked into the iframe ONCE at load; without this a dashboard left open
  // past the token's TTL sends an expired token → the gateway 401s → the
  // widget shows "Gateway HTTP 401" until reload. This re-mints and pushes
  // fresh tokens into the iframe (interval + on tab-focus + on the widget's
  // 401 self-heal request). See lib/otto/useOttoTokenRefresh.ts.
  const ottoEnv = (import.meta as { env?: Record<string, string> }).env ?? {};
  useOttoTokenRefresh(ottoOriginFromBase(ottoEnv.VITE_OTTO_BASE_URL));

  return (
    // RealtimeProvider opens the one always-on WebSocket for the session
    // (live notifications + presence + status); descendants read it via
    // useRealtime() — the profile-menu status selector does.
    // CallProvider sits UNDER RealtimeProvider so it can wire up the
    // call_invite / call_state socket frames via registerCallHandlers.
    <RealtimeProvider>
    <CallProvider>
    {/* AgenticProvider hosts the "Otto Agent" takeover — the launcher, the
        streamed thinking, and the cloak overlay — app-wide (like CallProvider).
        Staff-only: the launcher/cloak render only when the user has a permitted
        skill, and the gateway hard-gates the endpoint. See docs/otto-agent.md. */}
    <AgenticProvider>
    <CallUi />
    <OttoAgentLauncher />
    <CloakOverlay />
      {/* Subtle NEUTRAL page-depth gradient behind the whole authed app so
          the cards don't sit on a dead-flat canvas — built from the theme
          surface tokens (was the .bg-depth utility / --bg-depth token). */}
      <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundImage: (t) =>
          `radial-gradient(120% 90% at 50% 30%, ${t.palette.background.paper} 0%, ${t.palette.background.default} 48%, ${t.palette.surfaceSoft} 100%)`,
      }}
    >
      {/* Single global navigation loading bar — fires on every route
          change anywhere in the app, including inside /crm. */}
      <TopLoadingBar />
      {/* Brand mark on the left, section tabs centered as quiet monochrome
          pills, profile menu on the right. Translucent + blurred so content
          scrolls under it. */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: (t) => alpha(t.palette.background.paper, 0.85),
          backdropFilter: "blur(12px)",
          borderBottom: 1,
          borderColor: "divider",
          zIndex: (t) => t.zIndex.appBar,
        }}
      >
        <Box sx={{ display: "flex", width: "100%", minHeight: 56, alignItems: "center", gap: { xs: 0.5, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
          {isMobileNav && (
            <IconButton
              aria-label="Open navigation menu"
              onClick={() => setNavOpen(true)}
              edge="start"
              sx={{ color: "text.secondary", flexShrink: 0 }}
            >
              <MenuRoundedIcon />
            </IconButton>
          )}
          <BrandMark />
          {!isMobileNav && (
            <Box
              component="nav"
              aria-label="Primary"
              sx={{
                display: "flex",
                minWidth: 0,
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
                py: 1,
              }}
            >
              {/* Segmented "tray": the section tabs live inside one soft,
                * hairline-bordered pill group so the nav reads as a defined
                * control instead of loose text lost in the translucent bar.
                * Idle tabs are readable slate; the active tab is a lifted white
                * pill (see NavTab). Desktop only — below `md` the same sections
                * move into the slide-in Drawer (mobile can't fit the tray). */}
              <Box
                data-testid="primary-nav-tray"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2px",
                  maxWidth: "100%",
                  p: "3px",
                  borderRadius: 9999,
                  bgcolor: "surfaceSoft",
                  border: 1,
                  borderColor: "divider",
                  overflowX: "auto",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {SECTIONS.map((s) =>
                  s === "chat" ? (
                    <ChatTab key={s} />
                  ) : s === "crm" ? (
                    <CrmTab key={s} overdue={overdue} />
                  ) : (
                    <SectionTab key={s} section={s} />
                  ),
                )}
              </Box>
            </Box>
          )}
          {isMobileNav && <Box sx={{ flex: 1 }} />}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <CallLauncherButton />
            <NotificationBell />
            <ProfileMenu user={user} />
          </Stack>
        </Box>
      </AppBar>

      {/* Mobile navigation drawer — the section list for < md, where the
          horizontal tray doesn't fit. Temporary (children mount only while
          open), so it adds no links to the DOM on desktop / in unit tests. */}
      <Drawer anchor="left" open={navOpen} onClose={() => setNavOpen(false)}>
        <Box sx={{ width: 268 }} role="presentation">
          <Box sx={{ px: 2, py: 1.75, display: "flex", alignItems: "center", gap: 1 }}>
            <Box component="img" src="/xop-mark.svg" alt="" aria-hidden sx={{ height: 18, width: 18 }} />
            <Typography component="span" sx={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", color: "text.primary" }}>
              XOP Networks
            </Typography>
          </Box>
          <Divider />
          <List component="nav" aria-label="Primary" sx={{ py: 1 }}>
            {SECTIONS.map((s) => (
              <MobileNavItem
                key={s}
                section={s}
                overdue={s === "crm" ? overdue : null}
                onNavigate={() => setNavOpen(false)}
              />
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Content row: the page Outlet + the global right-edge presence rail.
          The rail is a slim sticky flex child (md+), so it reserves its own
          width and never overlaps content. */}
      <Box sx={{ flex: 1, display: "flex", minHeight: 0, width: "100%" }}>
        <Box component="main" sx={{ flex: 1, minWidth: 0, px: { xs: 3, lg: 4 }, py: 3 }}>
          {/* Route-scoped error boundary: a render throw on any page shows a
              "something went wrong" surface here (with Reload) instead of
              blanking the whole app, and the nav + shell stay usable. It
              auto-resets when the route changes. See ErrorBoundary.tsx. */}
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </Box>
        <PresenceRail />
      </Box>
      <Toaster />
      </Box>
    </AgenticProvider>
    </CallProvider>
    </RealtimeProvider>
  );
}

/**
 * Build the staff Otto loader `<script>` for the given base URL + a
 * pre-minted chat token, or `null` when either is missing (the widget
 * then simply doesn't ship). Pulled out as a pure helper because
 * `import.meta.env` is inlined at build time and can't be varied at
 * runtime, so the inject-only-when-configured logic is unit-tested
 * through this seam (AppShell.test.tsx); the env wiring is pinned by
 * compose-otto-env.test.ts.
 *
 * NOTE the deliberate omissions, both learned from the working customer
 * OttoWidget:
 *   - `data-api` MUST be the full base URL (with its `/otto` path) so the
 *     iframe loads `…/otto/chat`, not `…/chat` (the script *origin* drops
 *     the path).
 *   - NO `?bot=` query. With a bot key present the chat-bot runs a per-bot
 *     Origin check that rejects the prod same-origin `/otto` iframe
 *     (OTTO_PUBLIC_ORIGIN carries a `/otto` path the Origin header lacks).
 *     The bot is selected by the signed chat token instead.
 */
export function buildOttoLoaderScript(
  baseUrlRaw: string | undefined,
  chatToken: string | undefined,
): HTMLScriptElement | null {
  const baseUrl = (baseUrlRaw ?? "").replace(/\/$/, "");
  const token = chatToken ?? "";
  if (!baseUrl || !token) return null;
  const s = document.createElement("script");
  s.src = `${baseUrl}/embed.js`;
  s.async = true;
  s.setAttribute("data-xop-otto-loader", "1");
  s.setAttribute("data-api", baseUrl);
  s.setAttribute("data-persona", "support");
  s.setAttribute("data-chat-token", token);
  s.setAttribute("data-title", "Staff Otto");
  // Opt this host into parent-driven token refresh: the dashboard re-mints
  // the short-lived session token and pushes it into the iframe, and the
  // widget may ask us for a fresh one when the gateway 401s an expired
  // token (then replays the message). Absent this flag the widget leaves
  // its token-handling untouched — so the customer portal is unaffected.
  s.setAttribute("data-token-refresh", "1");
  // Enable the agentic "Otto Agent" affordance INSIDE the staff widget: the
  // widget shows a sparkle button that posts `otto:agent-run` to this dashboard,
  // which runs the agent (cloak + form pre-fill). This flag is set ONLY here (the
  // staff injector) — the customer portal never sets it, so customer Otto never
  // gets the agent. See docs/otto-agent.md + docs/otto-bots.md.
  s.setAttribute("data-agentic", "1");
  return s;
}

/**
 * Linear-style nav pill. Idle: muted, no bg. Hover: subtle soft-surface +
 * fg lift. Active (NavLink's auto `.active` class): brand-tinted soft pill
 * + brand text + 600 weight. styled(NavLink) keeps react-router's routing
 * props and the auto active class.
 */
const NavTab = styled(NavLink)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 9999,
  padding: "6px 12px",
  fontSize: 14,
  fontWeight: 500,
  whiteSpace: "nowrap",
  textDecoration: "none",
  // Idle tabs are readable slate (was text.disabled — too faint, which is
  // exactly why the nav got lost in the white bar), transparent so the
  // grey tray shows through.
  color: theme.palette.text.secondary,
  transition: theme.transitions.create(["background-color", "color", "box-shadow"], {
    duration: 150,
  }),
  // Hover only lifts the NON-active tabs (a faint fill on the tray); the
  // active pill keeps its own treatment below.
  "&:hover:not(.active)": {
    backgroundColor: alpha(theme.palette.text.primary, 0.06),
    color: theme.palette.text.primary,
  },
  // Active = a solid WHITE pill that lifts off the grey tray (soft shadow),
  // with the brand colour + heavier weight. Unmistakable at a glance.
  "&.active": {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    fontWeight: 600,
    boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.12)}, 0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 1,
  },
}));

function SectionTab({ section }: { section: Section }): JSX.Element | null {
  const visible = useCanSeeSection(section);
  if (!visible) return null;

  return (
    <NavTab
      to={pathFor(section)}
      end={section === "dashboard"}
      data-permission={SECTION_VIEW_PERMISSION[section]}
    >
      {SECTION_LABELS[section]}
    </NavTab>
  );
}

/**
 * The CRM tab — a `SectionTab` that also carries the overdue-invoice alert:
 * a red dot, and a red wave that sweeps the pill and breaks on that dot every
 * few seconds. Both appear ONLY when this operator has invoices past due that
 * they are allowed to see and act on (`crm:view` + `crm:financials`, row-scoped
 * per actor by crm-api) — a rep whose own invoices are all current gets an
 * ordinary tab. See OverdueNavAlert.tsx + lib/crm/overdueBadge.ts.
 */
function CrmTab({ overdue }: { overdue: OverdueBadge }): JSX.Element | null {
  const visible = useCanSeeSection("crm");
  if (!visible) return null;

  const label = SECTION_LABELS.crm;
  return (
    <NavTab
      to={pathFor("crm")}
      data-permission={SECTION_VIEW_PERMISSION.crm}
      data-overdue={overdue.active ? overdue.severity : undefined}
      {...(overdue.active ? { sx: overdueTabSx(overdue.severity) } : {})}
    >
      {overdue.active ? (
        <>
          <OverdueNavAlert totals={overdue.totals} severity={overdue.severity}>
            {label}
          </OverdueNavAlert>
          <OverdueWave severity={overdue.severity} />
        </>
      ) : (
        label
      )}
    </NavTab>
  );
}

/**
 * The Chat tab — a `SectionTab` that also carries a live unread badge. It
 * reads the same `chat/overview` query the Chat page does (one shared,
 * deduped fetch); the realtime provider invalidates that query on every
 * incoming message, so the count updates without the page being open. The
 * query is disabled when the user can't see Chat (avoids a pointless 403).
 */
function ChatTab(): JSX.Element | null {
  const visible = useCanSeeSection("chat");
  const overview = useChatOverview(visible);
  if (!visible) return null;
  const unread = overview.data?.total_unread ?? 0;

  return (
    <NavTab to={pathFor("chat")} data-permission={SECTION_VIEW_PERMISSION.chat}>
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
        {SECTION_LABELS.chat}
        {unread > 0 && (
          <Box
            component="span"
            data-testid="chat-nav-unread"
            sx={{
              minWidth: 18,
              height: 18,
              px: 0.5,
              borderRadius: 9999,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontSize: 10.5,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {unread > 99 ? "99+" : unread}
          </Box>
        )}
      </Box>
    </NavTab>
  );
}

/**
 * Mobile drawer nav item — the same permission-gated section link as
 * `SectionTab`, but a full-width 48px `ListItemButton` row (a real touch
 * target) with an active brand-tinted highlight. Closes the drawer on tap.
 */
function MobileNavItem({
  section,
  onNavigate,
  overdue = null,
}: {
  section: Section;
  onNavigate: () => void;
  /** Non-null only for the CRM row, and only when this operator has overdue
   * invoices they may see — the same alert the desktop tab shows, minus the
   * wave (a full-width list row has nothing pill-shaped to sweep). */
  overdue?: OverdueBadge | null;
}): JSX.Element | null {
  const visible = useCanSeeSection(section);
  if (!visible) return null;

  return (
    <ListItemButton
      component={NavLink}
      to={pathFor(section)}
      end={section === "dashboard"}
      onClick={onNavigate}
      data-permission={SECTION_VIEW_PERMISSION[section]}
      sx={{
        minHeight: 48,
        mx: 1,
        px: 2,
        borderRadius: 1,
        color: "text.secondary",
        "&.active": {
          bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
          color: "primary.main",
        },
        "&.active .MuiListItemText-primary": { fontWeight: 600 },
      }}
    >
      <ListItemText
        primary={
          overdue?.active ? (
            <OverdueNavAlert totals={overdue.totals} severity={overdue.severity}>
              {SECTION_LABELS[section]}
            </OverdueNavAlert>
          ) : (
            SECTION_LABELS[section]
          )
        }
      />
    </ListItemButton>
  );
}

/**
 * Brand mark — the compact `xop-mark.svg` swirl with "XOP Networks" as
 * plain bold text. The wordmark stays live HTML text (not baked into the
 * SVG) so it inherits the foreground colour when the theme flips.
 */
const BrandLink = styled(NavLink)(({ theme }) => ({
  display: "flex",
  minWidth: 0,
  alignItems: "center",
  gap: 8,
  borderRadius: 10,
  textDecoration: "none",
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 1,
  },
}));

function BrandMark(): JSX.Element {
  return (
    <BrandLink to="/" aria-label="XOP Networks — Command Center">
      <Box
        component="img"
        src="/xop-mark.svg"
        alt=""
        aria-hidden
        data-testid="brand-mark"
        draggable={false}
        // 24px = 20% larger than the prior 20px (the wordmark text is
        // unchanged — just the swirl ball). Still fits the app-bar height
        // alongside the stacked wordmark + tagline.
        sx={{ height: 24, width: 24, flexShrink: 0 }}
      />
      {/* Wordmark + "command center" STACKED (was a single inline row split
          by a vertical divider). The subtitle now sits UNDER the wordmark as
          a small uppercase tagline; tight line-heights keep both lines inside
          the app-bar height. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: 0,
        }}
      >
        <Typography
          component="span"
          data-testid="brand-wordmark"
          sx={{
            whiteSpace: "nowrap",
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: "-0.01em",
            color: "text.primary",
          }}
        >
          XOP Networks
        </Typography>
        <Typography
          component="span"
          data-testid="brand-tagline"
          sx={{
            display: { xs: "none", sm: "block" },
            whiteSpace: "nowrap",
            fontSize: 9,
            fontWeight: 600,
            lineHeight: 1.15,
            textTransform: "uppercase",
            letterSpacing: "0.18em",
            color: "text.disabled",
          }}
        >
          command center
        </Typography>
      </Box>
    </BrandLink>
  );
}

export function pathFor(section: Section): string {
  return section === "dashboard" ? "/" : `/${section}`;
}
